import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import RichContentDisplay from '../../components/content/RichContentDisplay';
import RichTextEditor from '../../components/content/RichTextEditor';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { forumService } from '../../services/forumService';
import { supabase } from '../../services/supabaseClient';
import { sanitizeContent } from '../../utils/contentSanitizer';
import { showToast } from '../../utils/toast';
import type { ForumTopic, ForumPost, ForumCategory, ForumPoll as ForumPollType, ForumPollOption } from '../../types/forum';
import ForumPoll from '../../components/forum/ForumPoll';
import '../../styles/pages/ForumTopicRefactored.css';

// Helper to format relative time
const formatRelativeTime = (dateString: string, t: (key: string, params?: any) => string) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return t('forums.time.secondsAgo', { count: diffInSeconds });
  if (diffInSeconds < 3600) return t('forums.time.minutesAgo', { count: Math.floor(diffInSeconds / 60) });
  if (diffInSeconds < 86400) return t('forums.time.hoursAgo', { count: Math.floor(diffInSeconds / 3600) });
  return t('forums.time.daysAgo', { count: Math.floor(diffInSeconds / 86400) });
};

// Helper to format join date
const formatJoinDate = (dateString: string, t: (key: string, params?: any) => string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays < 7) {
    const time = diffInDays === 0 ? t('forums.time.today') : diffInDays === 1 ? t('forums.time.yesterday') : t('forums.time.daysAgo', { count: diffInDays });
    return t('forums.time.joined', { time });
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return t('forums.time.joined', { time: `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago` }); // TODO: Add week/month/year keys
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return t('forums.time.joined', { time: `${months} ${months === 1 ? 'month' : 'months'} ago` });
  } else {
    const years = Math.floor(diffInDays / 365);
    return t('forums.time.joined', { time: `${years} ${years === 1 ? 'year' : 'years'} ago` });
  }
};

const ForumsTopicPage: React.FC = () => {
  const { categorySlug, topicId } = useParams<{ categorySlug: string; topicId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  const [category, setCategory] = useState<ForumCategory | null>(null);
  const [topic, setTopic] = useState<ForumTopic | null>(null);
  const [posts, setPosts] = useState<(ForumPost & { liked_by_user?: boolean })[]>([]);
  const [poll, setPoll] = useState<ForumPollType | null>(null);
  const [pollOptions, setPollOptions] = useState<ForumPollOption[]>([]);
  const [userVote, setUserVote] = useState<string | undefined>(undefined);
  const [totalVotes, setTotalVotes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [newPostContent, setNewPostContent] = useState('');
  const [isPostingReply, setIsPostingReply] = useState(false);
  const [postSubmissionSuccess, setPostSubmissionSuccess] = useState(false);
  const [postsPerPage, setPostsPerPage] = useState(20); // Make posts per page configurable
  const [isPinning, setIsPinning] = useState(false);

  const isAdmin = user?.role === 'admin';

  // Debug function to check current like status
  const debugLikeStatus = async (postId: string) => {
    if (!user) {
      console.log('🔍 Debug: No user logged in');
      showToast('Debug: No user logged in', 'info');
      return;
    }

    console.log('🔍 Debug: Checking like status for post:', postId);
    showToast('Debugging like status... Check console', 'info');

    try {
      // Check database directly
      const { data: likeExists, error } = await supabase
        .from('forum_post_likes')
        .select('id, created_at')
        .eq('post_id', postId)
        .eq('user_id', user.id)
        .single();

      // Check local state
      const post = posts.find(p => p.id === postId);

      console.log('📊 Like status debug:', {
        postId,
        userId: user.id,
        databaseHasLike: !!likeExists,
        localLikedByUser: post?.liked_by_user,
        localLikeCount: post?.like_count,
        databaseError: error?.message
      });

      // Also check the post's current like count from database
      const { data: postData } = await supabase
        .from('forum_posts')
        .select('like_count')
        .eq('id', postId)
        .single();

      console.log('💾 Database like count:', postData?.like_count);

      // Show summary in toast
      const summary = `DB: ${postData?.like_count || 0} likes, User liked: ${!!likeExists}, Local: ${post?.like_count || 0}`;
      showToast(summary, 'info');

    } catch (error) {
      console.error('❌ Error checking like status:', error);
      showToast('Debug failed - check console', 'error');
    }
  };

  // Handle pin/unpin topic
  const handleTogglePin = async (topicId: string, currentPinStatus: boolean) => {
    if (!isAdmin || isPinning) return;

    try {
      setIsPinning(true);

      await forumService.pinTopic(topicId, !currentPinStatus);

      // Update the topic in the current state
      setTopic(prevTopic =>
        prevTopic ? { ...prevTopic, is_pinned: !currentPinStatus } : null
      );

      showToast(
        t('forums.topic.togglePinSuccess', { status: !currentPinStatus ? t('forums.topic.pinned') : t('forums.topic.unpinned') }),
        'success'
      );
    } catch (error) {
      console.error('Error toggling pin status:', error);
      showToast(t('forums.topic.togglePinFailed'), 'error');
    } finally {
      setIsPinning(false);
    }
  };

  // Helper function to get all current post IDs for like subscription filtering
  const getCurrentPostIds = useCallback(() => {
    return posts.map(post => post.id);
  }, [posts]);

  const loadData = useCallback(async () => {
    if (!topicId) return;
    setLoading(true);
    try {
      // Fetch topic with poll and posts (category is included in topic)
      const combinedData = await forumService.getTopicWithPollAndPosts(topicId, page, postsPerPage);

      if (!combinedData.topic) {
        console.error('Topic not found for id:', topicId);
        navigate('/forums');
        return;
      }

      // Set all state at once
      setTopic(combinedData.topic);
      setPosts(combinedData.posts);
      setTotalPosts(combinedData.totalPosts);

      // Set poll data if exists
      if (combinedData.poll) {
        setPoll(combinedData.poll.poll);
        setPollOptions(combinedData.poll.options);
        setUserVote(combinedData.poll.userVote);
        const total = combinedData.poll.options.reduce((sum: number, option: ForumPollOption) => sum + option.vote_count, 0);
        setTotalVotes(total);
      }

      // Set category from topic (it's already included in topic response)
      if (combinedData.topic.category) {
        setCategory(combinedData.topic.category as ForumCategory);
      }

      // Increment view count (non-blocking, runs in background)
      forumService.incrementTopicViews(topicId);

    } catch (error) {
      console.error('Error loading topic data:', error);
      navigate('/forums');
    } finally {
      setLoading(false);
    }
  }, [topicId, page, postsPerPage, navigate]);

  const loadPagePosts = useCallback(async (pageNum: number) => {
    if (!topicId) return;
    setPostsLoading(true);
    try {
      const { posts: postsData, totalCount } = await forumService.getPostsWithEngagement(topicId, pageNum, postsPerPage);
      setPosts(postsData);
      setTotalPosts(totalCount);
    } catch (error) {
      console.error('Error loading posts:', error);
      setPosts([]);
      setTotalPosts(0);
    } finally {
      setPostsLoading(false);
    }
  }, [topicId, postsPerPage]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load posts when page changes (but not on initial load since loadData handles page 1)
  useEffect(() => {
    if (topic && page > 1) {
      loadPagePosts(page);
    }
  }, [page, topic, loadPagePosts]);

  // Real-time subscription for new posts - Fixed dependencies to prevent re-subscriptions
  useEffect(() => {
    if (!topicId) return;

    let postsSubscription: any = null;
    let authSubscription: any = null;

    const setupPostsSubscription = () => {
      // Clean up existing subscription first
      if (postsSubscription) {
        postsSubscription.unsubscribe();
      }

      postsSubscription = supabase
        .channel(`posts:${topicId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'forum_posts'
          },
          async (payload: any) => {
            // Check if this post belongs to our topic
            if (payload.new && payload.new.topic_id === topicId) {
              // Get the new post data with author information
              try {
                const newPostData = await forumService.getPostByIdFresh(payload.new.id);
                if (newPostData) {
                  // Add the new post to our local state for real-time updates
                  setPosts(prevPosts => {
                    // Check if post already exists (to avoid duplicates from optimistic updates)
                    const exists = prevPosts.some(post => post.id === newPostData.id);
                    if (exists) {
                      return prevPosts; // Don't add duplicate
                    }

                    // Create the post with proper like count and author information
                    const postWithLikes = {
                      ...newPostData,
                      liked_by_user: false,
                      like_count: newPostData.like_count || 0
                    };

                    return [...prevPosts, postWithLikes];
                  });

                  // Update total posts count
                  setTotalPosts(prev => prev + 1);

                  // For current page view, always show new posts at the end
                  setPosts(prevPosts => {
                    const exists = prevPosts.some(post => post.id === newPostData.id);
                    if (exists) {
                      return prevPosts;
                    }

                    // Create the post with proper like count and author information
                    const postWithLikes = {
                      ...newPostData,
                      liked_by_user: false,
                      like_count: newPostData.like_count || 0
                    };

                    return [...prevPosts, postWithLikes];
                  });
                }
              } catch (error) {
                // Fallback to full reload if we can't get the post details
                setTimeout(() => {
                  loadPagePosts(page);
                }, 1000);
              }
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'forum_posts',
            filter: `topic_id=eq.${topicId}`
          },
          async (payload: any) => {
            // Update the specific post in posts array
            try {
              const updatedPostData = await forumService.getPostByIdFresh(payload.new.id);
              if (updatedPostData) {
                setPosts(prevPosts =>
                  prevPosts.map(post =>
                    post.id === updatedPostData.id
                      ? {
                        ...updatedPostData,
                        liked_by_user: post.liked_by_user,
                        // Preserve existing author information if it has post_count and the new data doesn't
                        author: updatedPostData.author?.post_count || !post.author?.post_count ?
                          updatedPostData.author : post.author
                      }
                      : post
                  )
                );

                setPosts(prevPosts =>
                  prevPosts.map(post =>
                    post.id === updatedPostData.id
                      ? {
                        ...updatedPostData,
                        liked_by_user: post.liked_by_user,
                        // Preserve existing author information if it has post_count and the new data doesn't
                        author: updatedPostData.author?.post_count || !post.author?.post_count ?
                          updatedPostData.author : post.author
                      }
                      : post
                  )
                );
              }
            } catch (error) {
              console.error('❌ Real-time: Error fetching updated post details:', error);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'forum_posts',
            filter: `topic_id=eq.${topicId}`
          },
          (payload: any) => {
            const deletedPostId = payload.old.id;

            // Remove from posts array
            setPosts(prevPosts => prevPosts.filter(post => post.id !== deletedPostId));
            setPosts(prevPosts => prevPosts.filter(post => post.id !== deletedPostId));

            // Update total posts count
            setTotalPosts(prev => Math.max(0, prev - 1));
          }
        )
        .subscribe();
    };

    // Initial setup
    setupPostsSubscription();

    // Listen for auth state changes (including token refresh)
    authSubscription = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Auth state change in ForumsTopicPage:', event);

      if (event === 'TOKEN_REFRESHED') {
        console.log('🔄 Token refreshed, resubscribing to posts channel...');
        setupPostsSubscription();
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 User signed out, cleaning up posts subscription');
        if (postsSubscription) {
          postsSubscription.unsubscribe();
        }
      }
    });

    return () => {
      if (postsSubscription) {
        postsSubscription.unsubscribe();
      }
      if (authSubscription) {
        authSubscription.subscription?.unsubscribe();
      }
    };
  }, [topicId, loadPagePosts, page]);

  // Real-time subscription for post likes
  useEffect(() => {
    if (!topicId || posts.length === 0) return;

    const postIds = getCurrentPostIds();
    if (postIds.length === 0) return;

    console.log('🔔 Setting up real-time subscription for likes on posts:', postIds);

    let likeSubscription: any = null;
    let authSubscription: any = null;

    const setupLikesSubscription = () => {
      // Clean up existing subscription first
      if (likeSubscription) {
        likeSubscription.unsubscribe();
      }

      likeSubscription = supabase
        .channel(`likes:${topicId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'forum_post_likes'
          },
          async (payload: any) => {
            console.log('🔔 Real-time: Like added:', payload);

            // Check if this like is for one of our current posts
            const postId = payload.new?.post_id;
            if (postId && postIds.includes(postId)) {
              console.log('✅ Like is for current page post:', postId);

              // Get the current user to check if this is their like
              const { data: { user: currentUser } } = await supabase.auth.getUser();
              const isCurrentUserLike = currentUser?.id === payload.new?.user_id;

              console.log('🔍 Like analysis:', {
                postId,
                likedByUserId: payload.new?.user_id,
                currentUserId: currentUser?.id,
                isCurrentUserLike
              });

              // Skip real-time update if this is the current user's action
              // (their UI is already updated by handleLikePost)
              if (isCurrentUserLike) {
                console.log('⏭️ Skipping real-time update for current user\'s like');
                return;
              }

              console.log('🔄 Updating like count via real-time subscription');
              // Update like count for the specific post (only for other users' likes)
              setPosts(prevPosts =>
                prevPosts.map(post =>
                  post.id === postId
                    ? {
                      ...post,
                      like_count: (post.like_count || 0) + 1
                    }
                    : post
                )
              );

              setPosts(prevPosts =>
                prevPosts.map(post =>
                  post.id === postId
                    ? {
                      ...post,
                      like_count: (post.like_count || 0) + 1
                    }
                    : post
                )
              );
            } else {
              console.log('⏭️ Like not for current page posts, ignoring');
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'forum_post_likes'
          },
          async (payload: any) => {
            console.log('🔔 Real-time: Like removed:', payload);

            // Check if this unlike is for one of our current posts
            const postId = payload.old?.post_id;
            if (postId && postIds.includes(postId)) {
              console.log('✅ Unlike is for current page post:', postId);

              // Get the current user to check if this is their unlike
              const { data: { user: currentUser } } = await supabase.auth.getUser();
              const isCurrentUserUnlike = currentUser?.id === payload.old?.user_id;

              console.log('🔍 Unlike analysis:', {
                postId,
                unlikedByUserId: payload.old?.user_id,
                currentUserId: currentUser?.id,
                isCurrentUserUnlike
              });

              // Skip real-time update if this is the current user's action
              // (their UI is already updated by handleLikePost)
              if (isCurrentUserUnlike) {
                console.log('⏭️ Skipping real-time update for current user\'s unlike');
                return;
              }

              console.log('🔄 Updating like count via real-time subscription (unlike)');
              // Update like count for the specific post (only for other users' unlikes)
              setPosts(prevPosts =>
                prevPosts.map(post =>
                  post.id === postId
                    ? {
                      ...post,
                      like_count: Math.max((post.like_count || 0) - 1, 0)
                    }
                    : post
                )
              );

              setPosts(prevPosts =>
                prevPosts.map(post =>
                  post.id === postId
                    ? {
                      ...post,
                      like_count: Math.max((post.like_count || 0) - 1, 0)
                    }
                    : post
                )
              );
            } else {
              console.log('⏭️ Unlike not for current page posts, ignoring');
            }
          }
        )
        .subscribe((status, err) => {
          console.log('📡 Like subscription status:', status);
          if (err) {
            console.error('❌ Like subscription error:', err);
          }
        });
    };

    // Initial setup
    setupLikesSubscription();

    // Listen for auth state changes (including token refresh)
    authSubscription = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Auth state change for likes subscription:', event);

      if (event === 'TOKEN_REFRESHED') {
        console.log('� Token refreshed, resubscribing to likes channel...');
        setupLikesSubscription();
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 User signed out, cleaning up likes subscription');
        if (likeSubscription) {
          likeSubscription.unsubscribe();
        }
      }
    });

    return () => {
      console.log('�🔌 Cleaning up like subscription');
      if (likeSubscription) {
        likeSubscription.unsubscribe();
      }
      if (authSubscription) {
        authSubscription.subscription?.unsubscribe();
      }
    };
  }, [topicId, posts, getCurrentPostIds]);

  const handlePostReply = async () => {
    if (!newPostContent.trim() || !topicId || !user || isPostingReply) return;

    setIsPostingReply(true);
    setPostSubmissionSuccess(false);

    // Store current content for potential rollback
    const currentContent = newPostContent;

    try {
      // Clear the form immediately for better UX
      setNewPostContent('');

      // Sanitize the rich content before saving
      const sanitizedContent = sanitizeContent(currentContent);

      // Create the post
      const newPost = await forumService.createPost({
        topic_id: topicId,
        content: sanitizedContent,
        attachments: [],
      });

      // Show success indication
      setPostSubmissionSuccess(true);

      // Create optimistic post with proper author data
      // Get current post count for the user
      const userPostCount = posts.filter(post => post.user_id === user.id).length + 1; // +1 for the new post

      const optimisticPost = {
        ...newPost,
        author: {
          id: user.id,
          username: user.username || user.email?.split('@')[0] || 'Anonymous',
          display_name: user.display_name || undefined,
          avatar_url: user.avatar_url || undefined,
          created_at: typeof user.created_at === 'string' ? user.created_at : new Date().toISOString(),
          post_count: userPostCount
        },
        liked_by_user: false,
        like_count: 0
      };

      // Add to posts array optimistically
      setPosts(prevPosts => {
        // Check if already exists to avoid duplicates
        const exists = prevPosts.some(post => post.id === newPost.id);
        if (exists) return prevPosts;
        return [...prevPosts, optimisticPost];
      });

      setPosts(prevPosts => {
        // Check if already exists to avoid duplicates
        const exists = prevPosts.some(post => post.id === newPost.id);
        if (exists) return prevPosts;
        return [...prevPosts, optimisticPost];
      });

      // Update total posts count
      setTotalPosts(prev => prev + 1);

      // Hide success message after 3 seconds
      setTimeout(() => {
        setPostSubmissionSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error posting reply:', error);

      // Restore the form content on error
      setNewPostContent(currentContent);

      // Show user-friendly error message
      showToast(t('forums.topic.replyFailed'), 'error');
    } finally {
      setIsPostingReply(false);
    }
  };

  const handleVote = async (optionId: string) => {
    if (!user || !poll) return;
    try {
      // Optimistically update UI
      setUserVote(optionId);
      setPollOptions(prev => prev.map(opt => {
        if (opt.id === optionId) {
          return { ...opt, vote_count: opt.vote_count + 1 };
        }
        return opt;
      }));
      setTotalVotes(prev => prev + 1);

      // await forumService.voteOnPoll(poll.id, optionId); // Removed to prevent double voting (handled in ForumPoll component)
    } catch (error) {
      console.error('Error voting on poll:', error);
      // Revert on error (could be improved)
      showToast('Failed to vote', 'error');
    }
  };

  // Add a state to force editor re-render
  const [editorKey, setEditorKey] = useState(0);

  // Function to handle replying to a specific post
  const handleReplyToPost = (post: ForumPost) => {
    console.log('🔄 Reply button clicked for post:', post.id);

    // Extract content without nested blockquotes for cleaner quotes
    const extractMainContent = (html: string) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;

      // Remove any nested blockquotes to avoid confusion
      const blockquotes = tmp.querySelectorAll('blockquote');
      blockquotes.forEach(bq => bq.remove());

      // Get the remaining text, preserving line breaks
      let text = tmp.innerHTML;
      // Convert <p> tags to line breaks
      text = text.replace(/<\/p><p>/g, '\n');
      text = text.replace(/<p>/g, '').replace(/<\/p>/g, '\n');
      text = text.replace(/<br\s*\/?>/g, '\n');

      // Strip remaining HTML tags
      const cleanDiv = document.createElement('div');
      cleanDiv.innerHTML = text;
      const cleanText = cleanDiv.textContent || cleanDiv.innerText || '';

      // Trim and limit to first 200 chars for readability
      return cleanText.trim().substring(0, 200) + (cleanText.length > 200 ? '...' : '');
    };

    // Get clean text content without nested quotes
    const cleanContent = extractMainContent(post.content);

    // Format the timestamp
    const postDate = new Date(post.created_at);
    const formattedDate = postDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }) + ' ' + postDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Create blockquote content using rich text editor's quote format
    const quotedContent = `<blockquote>
<p><em>${post.author?.display_name || post.author?.username || t('forums.topic.anonymous')} ${t('forums.topic.postedAt')} ${formattedDate}:</em></p>
<p>${cleanContent}</p>
</blockquote>
<p><br></p>
<p></p>`;

    // Clear content first, then set new content and force re-render
    setNewPostContent('');
    setTimeout(() => {
      setNewPostContent(quotedContent);
      setEditorKey(prev => prev + 1); // Force editor re-render
      console.log('✅ Content set and editor re-rendered');
      console.log('📝 Current newPostContent state:', quotedContent);
    }, 50);

    // Scroll to reply form
    setTimeout(() => {
      const replyForm = document.querySelector('.reply-form');
      if (replyForm) {
        replyForm.scrollIntoView({ behavior: 'smooth' });

        // Focus on the editor after scrolling and position cursor at the end
        setTimeout(() => {
          const editorElement = document.querySelector('.reply-rich-editor .ProseMirror');
          if (editorElement) {
            (editorElement as HTMLElement).focus();

            // Position cursor at the end of the content
            const range = document.createRange();
            const selection = window.getSelection();

            // Select all content in the editor
            range.selectNodeContents(editorElement);
            // Collapse to the end (cursor at the end)
            range.collapse(false);

            // Clear any existing selection and set the new range
            selection?.removeAllRanges();
            selection?.addRange(range);

            console.log('✅ Focused on TipTap editor and positioned cursor at end');
          }
        }, 300);
      }
    }, 200);
  };

  // Function to handle liking posts (handleUnlikePost is integrated into handleLikePost)
  const handleLikePost = async (postId: string) => {
    console.log('🔄 Like button clicked for post:', postId);

    if (!user) {
      console.warn('❌ User not authenticated for liking post');
      showToast(t('forums.topic.loginToLike'), 'error');
      return;
    }

    // Check if post is already liked by the user
    const post = posts.find(p => p.id === postId);
    if (!post) {
      console.error('❌ Post not found in local state:', postId);
      return;
    }

    const isCurrentlyLiked = post?.liked_by_user;
    console.log(`🔍 Post ${postId} current like status:`, { isCurrentlyLiked, likeCount: post.like_count });

    // Prevent double-clicking with more robust checking
    if ((post as any).isLikeProcessing) {
      console.warn('⚠️ Like already in progress, ignoring click');
      return;
    }

    // Add a timestamp to track click intervals
    const now = Date.now();
    const lastClickTime = (post as any).lastLikeClickTime || 0;
    if (now - lastClickTime < 500) { // 500ms cooldown
      console.warn('⚠️ Click too soon after last click, ignoring');
      return;
    }

    try {
      console.log(`🚀 Starting ${isCurrentlyLiked ? 'unlike' : 'like'} operation for post ${postId}`);

      // Mark post as processing to prevent double clicks
      const markProcessing = (prevPosts: (ForumPost & { liked_by_user?: boolean })[]) =>
        prevPosts.map(p =>
          p.id === postId ? {
            ...p,
            isLikeProcessing: true,
            lastLikeClickTime: now
          } : p
        );

      setPosts(markProcessing);
      setPosts(markProcessing);

      // Verify user is still authenticated before making API call
      const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !currentUser) {
        throw new Error('Authentication failed. Please log in again.');
      }

      // Make API call and update UI immediately for better UX
      if (isCurrentlyLiked) {
        console.log('➖ Calling unlike API');
        await forumService.unlikePost(postId);
        console.log('✅ Unlike API call successful');

        // Update UI after successful API call
        const updateAfterUnlike = (prevPosts: (ForumPost & { liked_by_user?: boolean })[]) =>
          prevPosts.map(p =>
            p.id === postId
              ? {
                ...p,
                liked_by_user: false,
                like_count: Math.max((p.like_count || 0) - 1, 0),
                isLikeProcessing: false,
                lastLikeClickTime: now
              }
              : p
          );

        setPosts(updateAfterUnlike);
        setPosts(updateAfterUnlike);
        showToast('Post unliked', 'success');
      } else {
        console.log('➕ Calling like API');
        await forumService.likePost(postId);
        console.log('✅ Like API call successful');

        // Update UI after successful API call
        const updateAfterLike = (prevPosts: (ForumPost & { liked_by_user?: boolean })[]) =>
          prevPosts.map(p =>
            p.id === postId
              ? {
                ...p,
                liked_by_user: true,
                like_count: (p.like_count || 0) + 1,
                isLikeProcessing: false,
                lastLikeClickTime: now
              }
              : p
          );

        setPosts(updateAfterLike);
        setPosts(updateAfterLike);
        showToast('Post liked!', 'success');
      }

    } catch (error: any) {
      console.error('❌ Error during like operation:', error);

      // Remove processing flag on error
      const removeProcessing = (prevPosts: (ForumPost & { liked_by_user?: boolean })[]) =>
        prevPosts.map(p =>
          p.id === postId ? {
            ...p,
            isLikeProcessing: false,
            lastLikeClickTime: now
          } : p
        );

      setPosts(removeProcessing);
      setPosts(removeProcessing);

      // Show specific error messages based on error type
      let errorMessage = 'Failed to update like. Please try again.';
      if (error.message?.includes('Authentication')) {
        errorMessage = 'Please log in again to like posts.';
      } else if (error.code === '23505') {
        errorMessage = 'You have already liked this post.';
      } else if (error.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection.';
      }

      showToast(errorMessage, 'error');
      console.error('📊 Like operation failed:', {
        postId,
        userId: user?.id,
        isCurrentlyLiked,
        errorCode: error.code,
        errorMessage: error.message
      });

      // Show user-friendly error message
      showToast('Failed to update like status. Please try again.', 'error');
    }
  };

  const totalPages = Math.ceil(totalPosts / postsPerPage);

  // Enhanced pagination helpers
  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    const delta = 2; // Number of pages to show around current page

    if (totalPages <= 7) {
      // Show all pages if total is 7 or less
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Add ellipsis if needed
      if (page > delta + 2) {
        pages.push('...');
      }

      // Add pages around current page
      const start = Math.max(2, page - delta);
      const end = Math.min(totalPages - 1, page + delta);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (page < totalPages - delta - 1) {
        pages.push('...');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage);

      // Scroll to top of the page to show the first post
      // Try to scroll to thread header, or fallback to window top
      const threadHeader = document.querySelector('.thread-header');
      if (threadHeader) {
        threadHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePageJump = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const target = event.target as HTMLInputElement;
      const jumpPage = parseInt(target.value);
      if (!isNaN(jumpPage)) {
        handlePageChange(jumpPage);
        target.blur();
        target.value = ''; // Clear the input after jump
      }
    }
  };

  // Keyboard navigation for pagination
  const handleKeyboardNavigation = (event: KeyboardEvent) => {
    // Only handle when not typing in input fields
    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      event.target instanceof HTMLSelectElement
    ) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
        if (page > 1) {
          handlePageChange(page - 1);
        }
        break;
      case 'ArrowRight':
        if (page < totalPages) {
          handlePageChange(page + 1);
        }
        break;
      case 'Home':
        if (event.ctrlKey && page !== 1) {
          handlePageChange(1);
        }
        break;
      case 'End':
        if (event.ctrlKey && page !== totalPages) {
          handlePageChange(totalPages);
        }
        break;
    }
  };

  // Add keyboard navigation
  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardNavigation);
    return () => {
      document.removeEventListener('keydown', handleKeyboardNavigation);
    };
  }, [page, totalPages]);

  // Scroll to top functionality
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Poll rendering is now handled by ForumPoll component



  if (loading) {
    return (
      <BasePageLayout>
        <div className="forum-topic-container">
          <div className="loading-state">
            <div className="loading-ring"></div>
            <h3>Loading Discussion</h3>
            <p>Fetching the latest posts and comments...</p>
          </div>
        </div>
      </BasePageLayout>
    );
  }

  if (!topic || !category) {
    return (
      <BasePageLayout>
        <div className="forum-topic-container">
          <div className="error-state">
            <h2>Topic not found</h2>
            <p>The topic you're looking for doesn't exist or has been removed.</p>
            <Link to="/forums" className="btn btn-primary">
              Back to Forums
            </Link>
          </div>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout maxWidth="none" background="white">
      <div className="forum-topic-container">
        {/* Simplified Header - Just breadcrumb navigation */}
        <div className="thread-header">
          {/* Breadcrumb Navigation */}
          <nav className="thread-breadcrumb">
            <Link to="/forums" className="breadcrumb-link">
              <i className="bi bi-house-door"></i>
              {t('forums.topic.breadcrumbs.forums')}
            </Link>
            <i className="bi bi-chevron-right breadcrumb-separator"></i>
            <Link
              to={`/forums/category/${categorySlug || category?.slug}`}
              className="breadcrumb-link category-link"
            >
              <i className="bi bi-arrow-left"></i>
              {category ? t(`forums.categories.titles.${category.slug}`, { defaultValue: category.title }) : ''}
            </Link>
            <i className="bi bi-chevron-right breadcrumb-separator"></i>
            <span className="breadcrumb-current">{topic?.title}</span>
          </nav>
        </div>

        {/* Top Controls Section */}
        <div className="topic-top-controls">
          <div className="topic-controls-left">
            {user && (
              <button
                className="new-post-btn"
                onClick={() => {
                  const replyForm = document.querySelector('.reply-form');
                  replyForm?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <i className="bi bi-plus-circle"></i>
                {t('forums.topic.buttons.newPost')}
              </button>
            )}
          </div>

          {/* Admin Controls */}
          {isAdmin && topic && (
            <div className="topic-admin-controls">
              <button
                className={`admin-pin-btn ${topic.is_pinned ? 'pinned' : ''}`}
                onClick={() => handleTogglePin(topic.id, topic.is_pinned)}
                disabled={isPinning}
                title={topic.is_pinned ? 'Unpin this topic' : 'Pin this topic'}
              >
                {isPinning ? (
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  <>
                    <i className={`bi ${topic.is_pinned ? 'bi-pin-angle-fill' : 'bi-pin-angle'}`}></i>
                    {topic.is_pinned ? 'Unpin Topic' : 'Pin Topic'}
                  </>
                )}
              </button>
            </div>
          )}

          <div className="topic-controls-right">
            {/* Top Pagination */}
            {totalPages > 1 && (
              <nav className="topic-pagination-top">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="pagination-btn prev"
                >
                  <i className="bi bi-chevron-left"></i>

                </button>

                <div className="pagination-numbers">
                  {generatePageNumbers().map((pageNum, index) => (
                    <React.Fragment key={index}>
                      {pageNum === '...' ? (
                        <span className="pagination-ellipsis">...</span>
                      ) : (
                        <button
                          onClick={() => handlePageChange(pageNum as number)}
                          className={`pagination-number ${page === pageNum ? 'active' : ''}`}
                        >
                          {pageNum}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="pagination-btn next"
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </nav>
            )}
          </div>
        </div>

        {/* Poll Section - Show at top before posts */}
        {poll && pollOptions.length > 0 && page === 1 && (
          <ForumPoll
            poll={poll}
            options={pollOptions}
            userVote={userVote}
            onVote={handleVote}
          />
        )}

        {/* Posts Table */}
        <div className="posts-table-container">
          {postsLoading && page === 1 ? (
            <div className="loading-state">
              <div className="loading-ring"></div>
              <h3>{t('forums.topic.loadingPosts')}</h3>
              <p>{t('forums.topic.loadingPostsSubtitle')}</p>
            </div>
          ) : posts.length > 0 ? (
            <div className="posts-table">
              {/* Topic Header Row - First row with topic info and stats */}
              <div className="topic-header-row">
                <div className="topic-stats-cell">
                  <div className="topic-stats-list">
                    <div className="topic-stat-item">
                      <i className="bi bi-eye"></i>
                      {topic.view_count || 0} {t('forums.topic.views')}
                    </div>
                    <div className="topic-stat-item">
                      <i className="bi bi-chat-dots"></i>
                      {totalPosts} {t('forums.topic.replies')}
                    </div>
                  </div>
                </div>
                <div className="topic-name-cell">
                  <h1 className="topic-title-in-table">
                    {topic.is_pinned && (
                      <span className="pin-indicator" title="Pinned topic">
                        <i className="bi bi-pin-angle-fill"></i>
                      </span>
                    )}
                    {topic.title}
                  </h1>
                  <div className="topic-meta-in-table">
                    <div className="topic-meta-item">
                      <i className="bi bi-person"></i>
                      {t('forums.topic.startedBy')} {topic.created_by_user?.display_name || topic.created_by_user?.username || t('forums.topic.anonymous')}
                    </div>
                    <div className="topic-meta-item">
                      <i className="bi bi-clock"></i>
                      {formatRelativeTime(topic.created_at, t)}
                    </div>
                  </div>
                  {/* Topic Tags */}
                  {topic.tags && topic.tags.length > 0 && (
                    <div className="topic-tags-in-table">
                      {topic.tags.map((tag, index) => (
                        <span key={index} className="topic-tag-in-table">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Posts rows */}
              {posts.map((post, index) => (
                <div key={post.id} className="post-table-row">
                  {/* Left Column - Author Info */}
                  <div className="post-author-column">
                    <div className="author-avatar">
                      <img
                        src={post.author?.avatar_url || '/icons/default-avatar.svg'}
                        alt={post.author?.username}
                        className="author-avatar-img"
                        onError={(e) => {
                          const target = e.currentTarget;
                          // Prevent infinite loop if default avatar also fails
                          if (target.src.includes('/icons/default-avatar.svg')) return;
                          target.src = '/icons/default-avatar.svg';
                        }}
                      />
                    </div>
                    <div className="author-details">
                      <div className="author-name">
                        {post.author?.display_name || post.author?.username || 'Anonymous'}
                      </div>
                      <div className="author-meta">
                        <div className="author-post-count">
                          {post.author?.post_count || 0} {t('forums.topic.authorPosts')}
                        </div>
                        <div className="author-join-date">
                          {post.author?.created_at ? formatJoinDate(post.author.created_at, t) : t('forums.topic.joinedRecently')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Post Content */}
                  <div className="post-content-column">
                    {/* Post Header Row */}
                    <div className="post-header-row">
                      <div className="post-time-and-author">
                        <span className="post-author-name">
                          {post.author?.display_name || post.author?.username || t('forums.topic.anonymous')}
                        </span>
                        <span className="post-time">
                          {formatRelativeTime(post.created_at, t)}
                        </span>
                      </div>
                      <div className="post-position">
                        #{((page - 1) * postsPerPage) + index + 1}
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="post-content-body">
                      <RichContentDisplay
                        content={post.content}
                        className="post-text-content"
                      />

                      {/* Show attachments */}
                      {post.attachments && post.attachments.length > 0 && (
                        <div className="post-attachments">
                          <div className="attachments-label">
                            <i className="bi bi-paperclip"></i>
                            {t('forums.topic.attachments')}
                          </div>
                          {post.attachments.map((attachment, idx) => (
                            <div key={idx} className="attachment-item">
                              <a href={attachment.download_url} target="_blank" rel="noopener noreferrer">
                                {attachment.filename}
                              </a>
                              <span className="attachment-size">
                                ({Math.round(attachment.size / 1024)}KB)
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Post Actions Row */}
                    <div className="post-actions-row">
                      <button
                        className="post-reply-btn"
                        onClick={() => handleReplyToPost(post)}
                        title="Reply to this post"
                      >
                        <i className="bi bi-reply"></i>
                        {t('forums.topic.buttons.reply')}
                      </button>
                      <button
                        className={`post-like-btn ${post.liked_by_user ? 'liked' : ''}`}
                        onClick={() => handleLikePost(post.id)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          debugLikeStatus(post.id);
                        }}
                        title={`${post.liked_by_user ? 'Unlike this post' : 'Like this post'} (Right-click to debug)`}
                        disabled={(post as any).isLikeProcessing}
                      >
                        {(post as any).isLikeProcessing ? (
                          <div className="loading-spinner-small"></div>
                        ) : (
                          <i className={`bi ${post.liked_by_user ? 'bi-heart-fill' : 'bi-heart'}`}></i>
                        )}
                        <span className="like-count">
                          {post.like_count || 0}
                        </span>
                      </button>
                      <button
                        className="post-share-btn"
                        onClick={() => {
                          const postUrl = `${window.location.origin}/forums/topic/${topicId}#post-${post.id}`;
                          navigator.clipboard.writeText(postUrl);
                          showToast('Post link copied to clipboard!', 'success');
                        }}
                        title="Copy link to this post"
                      >
                        <i className="bi bi-link-45deg"></i>
                        {t('forums.topic.buttons.share')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-posts-state" style={{ marginLeft: '10px' }}>
              <div className="empty-icon">💭</div>
              <h3>{t('forums.topic.noPostsYet')}</h3>
              <p>{t('forums.topic.beFirstToPost')}</p>
            </div>
          )}
        </div>

        {/* Bottom Controls Section */}
        <div className="topic-bottom-controls">

          <div className="topic-controls-right">
            {/* Bottom Pagination */}
            {totalPages > 1 && (
              <nav className="topic-pagination-bottom">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="pagination-btn prev"
                >
                  <i className="bi bi-chevron-left"></i>

                </button>

                <div className="pagination-numbers">
                  {generatePageNumbers().map((pageNum, index) => (
                    <React.Fragment key={index}>
                      {pageNum === '...' ? (
                        <span className="pagination-ellipsis">...</span>
                      ) : (
                        <button
                          onClick={() => handlePageChange(pageNum as number)}
                          className={`pagination-number ${page === pageNum ? 'active' : ''}`}
                        >
                          {pageNum}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  className="pagination-btn next"
                >

                  <i className="bi bi-chevron-right"></i>
                </button>
              </nav>
            )}
          </div>
        </div>

        {/* New Post Form */}
        {user ? (
          <div className="reply-form">
            <h3 className="reply-form-title">{t('forums.topic.form.title')}</h3>

            <div className="reply-user-header">
              <img
                src={user?.avatar_url || '/icons/default-avatar.svg'}
                alt={user?.username || 'You'}
                className="reply-user-avatar"
                onError={(e) => {
                  const target = e.currentTarget;
                  // Prevent infinite loop if default avatar also fails
                  if (target.src.includes('/icons/default-avatar.svg')) return;
                  target.src = '/icons/default-avatar.svg';
                }}
              />
              <div className="reply-user-info">
                <span className="reply-username">
                  {user?.display_name || user?.username || 'You'}
                </span>
                <span className="reply-user-status">
                  {t('forums.topic.form.postingAs')} {user?.display_name || user?.username || user?.email || t('forums.topic.anonymous')}
                </span>
              </div>
            </div>

            <RichTextEditor
              key={editorKey}
              content={newPostContent}
              onChange={setNewPostContent}
              placeholder={t('forums.topic.form.placeholder')}
              className="reply-rich-editor"
              topicId={topicId}
            />

            {/* Success Message */}
            {postSubmissionSuccess && (
              <div className="reply-success-message">
                <i className="bi bi-check-circle-fill"></i>
                <span>{t('forums.topic.form.success')}</span>
              </div>
            )}

            <div className="reply-actions">
              <button
                onClick={handlePostReply}
                disabled={!newPostContent.replace(/<[^>]*>/g, '').trim() || isPostingReply}
                className={`submit-reply-btn ${isPostingReply ? 'loading' : ''}`}
              >
                {isPostingReply ? (
                  <>
                    <div className="loading-spinner-small"></div>
                    <span>{t('forums.topic.buttons.creating')}</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-send-fill"></i>
                    {t('forums.topic.buttons.createPost')}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewPostContent('');
                  setPostSubmissionSuccess(false);
                  setEditorKey(prev => prev + 1);
                }}
                className="cancel-reply-btn"
                disabled={isPostingReply}
              >
                <i className="bi bi-arrow-clockwise"></i>
                {t('forums.topic.buttons.clearReset')}
              </button>
            </div>
          </div>
        ) : (
          <div className="login-prompt">
            <p>Please <Link to="/login">log in</Link> to participate in this discussion.</p>
          </div>
        )}
      </div>
    </BasePageLayout>
  );
};

export default ForumsTopicPage;